import React, { useState, useEffect, useCallback } from 'react';
import { setReportModalOpener, ErrorContext } from '../../utils/errorToast';
import ReportIssueModal from './ReportIssueModal';

interface ErrorToastProviderProps {
  children: React.ReactNode;
}

/**
 * ErrorToastProvider
 *
 * Wraps your app to enable the "Report Issue" functionality on error toasts.
 * Should be placed inside ThemeProvider but can be anywhere in the component tree.
 *
 * Usage:
 * ```tsx
 * // In App.tsx or your root component
 * import { ErrorToastProvider } from './components/common/ErrorToastProvider';
 *
 * function App() {
 *   return (
 *     <ThemeProvider>
 *       <ErrorToastProvider>
 *         {/* Your app content *\/}
 *       </ErrorToastProvider>
 *     </ThemeProvider>
 *   );
 * }
 * ```
 */
const ErrorToastProvider: React.FC<ErrorToastProviderProps> = ({ children }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentErrorContext, setCurrentErrorContext] = useState<ErrorContext | null>(null);

  // Handler to open the modal with error context
  const openReportModal = useCallback((context: ErrorContext) => {
    setCurrentErrorContext(context);
    setIsModalOpen(true);
  }, []);

  // Register the modal opener on mount
  useEffect(() => {
    setReportModalOpener(openReportModal);

    // Cleanup on unmount
    return () => {
      setReportModalOpener(() => {});
    };
  }, [openReportModal]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // Clear context after animation
    setTimeout(() => {
      setCurrentErrorContext(null);
    }, 300);
  };

  const handleSuccess = () => {
    // Optional: could add analytics or additional actions here
  };

  return (
    <>
      {children}
      {currentErrorContext && (
        <ReportIssueModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          errorContext={currentErrorContext}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
};

export default ErrorToastProvider;
